import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { listWorkflows, rollWorkflowShareId, deleteWorkflow, upsertWorkflow } from '@/src/api/workflow'
import { ShareLinkCell } from '@/src/components/manage/tables/CopyableCell'
import { Badge } from '@/src/components/ui/badge'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { DeleteAction } from '@/src/components/manage/actions/DeleteAction'
import { EnabledAction } from '@/src/components/manage/actions/EnabledAction'
import { CreateAction } from '@/src/components/manage/actions/CreateAction'
import { useTranslation } from '@/src/i18n/client'
import { toast } from 'sonner'
import type { Workflow } from '@/src/types/workflow.zod'

export const Route = createFileRoute('/_manage/manage/admin/workflows/')({
  component: AdminWorkflowsPage,
})

function AdminWorkflowsPage() {
  const { t } = useTranslation('manage');
  const queryClient = useQueryClient()
  
  const { data: allWorkflows, isLoading, error } = useQuery({
    queryKey: ['workflow', 'list', 'admin'],
    queryFn: () => listWorkflows({ data: { all: true, system: true } }),
  })

  const workflows = allWorkflows || []

  const rollShareIdMutation = useMutation({
    mutationFn: (workflowId: string) => rollWorkflowShareId({ data: { workflowId } }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteWorkflow({ data: { id } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow'] })
      toast.success(t('workflows.workflowDeleted'))
    },
    onError: (err: any) => {
      toast.error(err?.message || t('workflows.failedToDeleteWorkflow'))
    },
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ workflow, enabled }: { workflow: Workflow; enabled: boolean }) =>
      upsertWorkflow({
        data: {
          id: workflow.id,
          name: workflow.name,
          description: workflow.description ?? null,
          data: workflow.data,
          visibility: workflow.visibility,
          enabled,
          order: workflow.order,
          isSystem: workflow.isSystem,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow'] })
    },
  })

  const columns: ColumnConfig<Workflow>[] = [
    {
      key: 'shareId',
      label: t('table.shareId'),
      width: 'w-24',
      render: (workflow) => (
        <ShareLinkCell
          shareId={workflow.shareId}
          reserveActionSpace={true}
          canRotate={true}
          onRotate={async () => {
            await rollShareIdMutation.mutateAsync(workflow.id)
            await queryClient.invalidateQueries({ queryKey: ['workflow'] })
          }}
        />
      ),
    },
    {
      key: 'name',
      label: t('table.name'),
    },
    {
      key: 'description',
      label: t('table.description'),
      render: (workflow) => workflow.description || '-',
    },
    {
      key: 'visibility',
      label: t('table.visibility'),
      render: (workflow) => (
        <Badge
          variant={
            workflow.visibility === "PUBLIC"
              ? "default"
              : workflow.visibility === "ORGANIZATION"
                ? "secondary"
                : "outline"
          }
          className={
            workflow.visibility === "PUBLIC"
              ? "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-green-100 text-green-800 border-green-200"
              : workflow.visibility === "ORGANIZATION"
                ? "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-blue-100 text-blue-800 border-blue-200"
                : "mr-2 px-2 py-0.5 rounded-full text-xs font-semibold align-middle bg-gray-100 text-gray-800 border-gray-200"
          }
        >
          {workflow.visibility}
        </Badge>
      ),
    },
    {
      key: 'isSystem',
      label: t('workflows.systemLabel'),
      render: (workflow) => workflow.isSystem ? 
        <Badge className="bg-purple-100 text-purple-800 border-purple-200">{t('workflows.systemLabel')}</Badge> : 
        <span className="text-muted-foreground">-</span>,
    },
    {
      key: 'enabled',
      label: t('table.status'),
      align: 'right',
      render: (workflow) => (
        <EnabledAction
          enabled={!!workflow.enabled}
          onToggle={async (enabled) => {
            await toggleEnabledMutation.mutateAsync({ workflow, enabled })
          }}
        />
      ),
    },
  ];

  return (
    <div>
      <AdminToolbar>
        <AdminToolbar.Left>
          <CreateAction
            to="/manage/admin/workflows/$id"
            params={{ id: 'new' }}
            label={t('workflows.createSystemWorkflow')}
            size="lg"
          />
        </AdminToolbar.Left>
      </AdminToolbar>
      <div className="bg-background text-foreground">
        {error && (
          <p className="text-destructive">
            {error instanceof Error ? error.message : t('workflows.failedToLoadWorkflows')}
          </p>
        )}
        <DataTable<Workflow>
          data={(workflows as any)}
          columns={columns}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : undefined}
          emptyMessage={t('workflows.emptySystemMessage')}
          actions={(workflow) => (
            <>
              <EditAction
                to="/manage/admin/workflows/$id"
                params={{ id: workflow.id }}
              />
              <DeleteAction
                itemName={workflow.name}
                itemType="workflow"
                buttonLabel={t('workflows.deleteDialog.ariaDelete')}
                customTitle={t('workflows.deleteDialog.title')}
                customDescription={t('workflows.deleteDialog.confirm', { name: workflow.name })}
                onDelete={async () => {
                  await deleteMutation.mutateAsync(workflow.id)
                }}
                isPending={deleteMutation.isPending}
              />
            </>
          )}
        />
      </div>
    </div>
  )
}
